"use client";

import React, { useState, useEffect, Suspense } from 'react';
import { StoreProvider, useStore } from '@/context/StoreContext';
import { CartProvider, useCart } from '@/context/CartContext';
import CartPage from '@/components/Cart';
import { Toaster } from 'react-hot-toast';
import { AnalyticsListener } from '@/components/AnalyticsListener';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faInstagram, faFacebook, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons';
import { AuthProvider } from '@/context/AuthContext';
import WhatsAppFloat from '@/components/WhatsAppFloat';
import { getStoreOpenStatus } from '@/lib/formatters';

const XMarkIcon = (props: React.SVGProps<SVGSVGElement>) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>);

function ContactHeader() {
  const { settings } = useStore();

  // Oculta se for delivery/food
  if (settings.catalog_type === 'food') return null;

  const hasContact = settings.phone || settings.email;
  const hasSocial = settings.instagram_url || settings.facebook_url || settings.tiktok_url;
  const openStatus = getStoreOpenStatus(settings.horario_funcionamento);

  if (!hasContact && !hasSocial && !openStatus) return null;

  return (
    <div className="bg-[#181818] text-gray-300 text-xs py-2 hidden md:block border-b border-black/10">
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center gap-6 font-medium">
          {openStatus && (
            <span 
              title={openStatus.label}
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold border transition-colors ${
                openStatus.isOpen 
                  ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' 
                  : 'bg-white/10 text-gray-400 border-white/20'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${openStatus.isOpen ? 'bg-emerald-400 animate-pulse' : 'bg-gray-400'}`}></span>
              {openStatus.isOpen ? 'Aberto Agora' : 'Fechado'}
            </span>
          )}
          {settings.phone && (
            <>
              <a href={`https://wa.me/${settings.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                <FontAwesomeIcon icon={faWhatsapp} className="text-green-500 text-[14px]" /> WhatsApp
              </a>
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faPhone} className="text-gray-400" /> {settings.phone}
              </span>
            </>
          )}
          {settings.email && (
            <a href={`mailto:${settings.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
              <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" /> {settings.email}
            </a>
          )}
        </div>
        <div className="flex items-center gap-4">
          {settings.instagram_url && (
            <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="hover:text-white transition-colors">
              <FontAwesomeIcon icon={faInstagram} className="text-[14px]" />
            </a>
          )}
          {settings.facebook_url && (
            <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="hover:text-white transition-colors">
              <FontAwesomeIcon icon={faFacebook} className="text-[14px]" />
            </a>
          )}
          {settings.tiktok_url && (
            <a href={settings.tiktok_url} target="_blank" rel="noreferrer" className="hover:text-white transition-colors">
              <FontAwesomeIcon icon={faTiktok} className="text-[14px]" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function TopBar() {
  const { settings } = useStore();
  const [isVisible, setIsVisible] = useState(true);
  if (!settings.top_bar_is_active || !settings.top_bar_text || !isVisible) return null;

  return (
    <div className="relative z-50 px-4 py-2.5 text-center text-sm font-bold text-white transition-colors shadow-sm" style={{ backgroundColor: settings.primary_color || '#f97316' }}>
      <p>{settings.top_bar_text}</p>
      <button onClick={() => setIsVisible(false)} className="absolute top-1/2 right-4 -translate-y-1/2 p-1 text-white/80 hover:text-white transition-opacity" aria-label="Dispensar"><XMarkIcon className="h-5 w-5" /></button>
    </div>
  );
}

function MarketingPopup() {
  const { settings } = useStore();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hasDismissed = localStorage.getItem('popupDismissed');
      if (settings.popup_is_active && !hasDismissed) {
        setTimeout(() => setIsOpen(true), 2000);
      }
    }
  }, [settings.popup_is_active]);

  const handleClose = () => { setIsOpen(false); localStorage.setItem('popupDismissed', 'true'); };

  if (!isOpen || !settings.popup_text) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity" onClick={handleClose}></div>
      <div className="fixed top-1/2 left-1/2 z-50 w-[90%] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl transform transition-all scale-100">
        <button onClick={handleClose} className="absolute top-3 right-3 p-2 text-gray-400 hover:text-gray-800 bg-gray-100 rounded-full transition-colors"><XMarkIcon className="h-5 w-5" /></button>
        <div className="prose text-center text-gray-700 mt-2" dangerouslySetInnerHTML={{ __html: settings.popup_text }}></div>
        <div className="mt-6 text-center">
            <button onClick={handleClose} className="px-6 py-2 rounded-full font-bold text-white shadow-md hover:brightness-110 transition-all" style={{ backgroundColor: settings.primary_color || '#f97316' }}>Entendi</button>
        </div>
      </div>
    </>
  );
}

function DynamicThemeInjector() {
  const { settings } = useStore();
  const primary = settings.primary_color || '#f97316';
  const secondary = settings.secondary_color || '#ffffff';
  const tertiary = settings.tertiary_color || '#1f2937';

  return (
    <style dangerouslySetInnerHTML={{ __html: `
      :root {
        --primary: ${primary};
        --secondary: ${secondary};
        --tertiary: ${tertiary};
        --surface: ${secondary};
        --text-main: ${tertiary};
      }
      .swiper-pagination-bullet-active { background-color: var(--primary) !important; width: 18px; border-radius: 4px; transition: width 0.3s; }
      .swiper-button-next, .swiper-button-prev { color: var(--primary) !important; background: white; width: 40px; height: 40px; border-radius: 50%; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
      .swiper-button-next:after, .swiper-button-prev:after { font-size: 16px; font-weight: bold; }
    `}} />
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <StoreProvider>
      {/* 👇 ADICIONADO AQUI 👇 */}
      <AuthProvider>
        <CartProvider>
          <Toaster position="top-center" toastOptions={{ duration: 3000, style: { background: '#333', color: '#fff', borderRadius: '10px' } }} />
          
          <TopBar />
          <ContactHeader />
          
          <MarketingPopup />
          <WhatsAppFloat />
          <Suspense fallback={null}><AnalyticsListener /></Suspense>
          <AppContent>{children}</AppContent>
        </CartProvider>
      </AuthProvider>
    </StoreProvider>
  );
}

function AppContent({ children }: { children: React.ReactNode }) {
    const { isCartOpen } = useCart();
    const { settings } = useStore();
    const currentTemplate = settings.store_template || 'default';

    return (
        <div className="store-template-root" data-store-template={currentTemplate}>
            <DynamicThemeInjector />
            {isCartOpen ? <CartPage /> : children}
        </div>
    );
}